import { Component, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { FloorplanMonitoringItemDetailsModalComponent } from "@components/floorplan-monitoring-item-details-modal/floorplan-monitoring-item-details-modal.component";
import { Device } from "@domain/product/monitoring/device";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { MonitoringProductListAddService } from "@domain/project/monitoring-product-list/monitoring-product-list-add.service";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { isMonitoringProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { MonitoringSearchItemComponent } from "@project/floorplanner/floorplan-menu/monitoring/monitoring-search-item/monitoring-search-item.component";
import { SelectMonitoringPlaceholderModalComponent } from "@project/floorplanner/floorplan-menu/monitoring/select-monitoring-placeholder-modal/select-monitoring-placeholder-modal.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { createMonitoringGroupedSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import { getMonitoringPrimaryLabel, isGroupedOption } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  DeviceSearchItem,
  GroupSearchItem,
  MonitoringProductSearchService,
} from "@project/monitoring/monitoring-product-search.service";
import { MonitoringSearchResult } from "@project/monitoring/monitoring-search-result";
import { combineLatest, map } from "rxjs";
import { createMonitoringPlaceholder } from "./monitoring-product-search-menu.helpers";

@Component({
  selector: "app-monitoring-product-search-menu",
  templateUrl: "./monitoring-product-search-menu.component.html",
  styleUrl: "./monitoring-product-search-menu.component.scss",
  imports: [UiKitModule, FilterProductComponent, MonitoringSearchItemComponent],
  standalone: true,
})
export class MonitoringProductSearchMenuComponent {
  private monitoringProductSearchService = inject(MonitoringProductSearchService);
  private floorplanService = inject(FloorplanService);
  private modalService = inject(ModalService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private monitoringProductListAddService = inject(MonitoringProductListAddService);

  protected readonly canEditProductList = toSignal(
    combineLatest([this.userService.user$, this.projectService.hasUserWritePermission$]).pipe(
      map(([user, hasWritePermission]) => !user.readOnlyAccess && hasWritePermission === true),
    ),
    { initialValue: false },
  );

  private groups: GroupSearchItem[] = [];
  private devicesWithContext: DeviceSearchItem[] = [];
  private deviceLookup = new Map<string, Device>();

  protected filteredResults: MonitoringSearchResult[] = [];
  protected searchValue = "";

  constructor() {
    this.monitoringProductSearchService.loadSearchData().subscribe((data) => {
      this.groups = data.groups;
      this.devicesWithContext = data.devices;
      this.deviceLookup = data.lookup;
    });
  }

  protected search(value: string) {
    this.searchValue = value;
    if (!value) {
      this.filteredResults = [];
      return;
    }
    this.filteredResults = this.monitoringProductSearchService.search(
      value,
      this.groups,
      this.devicesWithContext,
      this.deviceLookup,
    );
  }

  protected addToFloorplan(result: MonitoringSearchResult) {
    if (result.canAddToFloorplan === false) return;
    const rawProject = this.floorplanService.selectedFloorplan.project;
    if (!isMonitoringProject(rawProject)) return;
    const project: MonitoringProject = rawProject;

    const name = result.kind === "device" ? result.device.name : result.option.groupName;
    const slotRole = result.kind === "device" ? result.slotRole : "head";
    const config = MonitoringConfigurationFactory.fromSearchResult(
      result.category,
      result.family,
      result.option,
      name,
      project,
      slotRole,
    );
    if (!config) return;

    switch (result.category) {
      case MonitoringCategory.PATIENT_MONITORS:
        project.addPatientMonitors(config as PatientMonitorConfiguration);
        this.floorplanService.addPatientMonitor(config as PatientMonitorConfiguration);
        break;
      case MonitoringCategory.CENTRAL_STATIONS:
        project.addCentralStations(config as CentralStationConfiguration);
        this.floorplanService.addCentralStation(config as CentralStationConfiguration);
        break;
      case MonitoringCategory.TELEMETRY:
        project.addTelemetries(config as TelemetryConfiguration);
        this.floorplanService.addTelemetry(config as TelemetryConfiguration);
        break;
      case MonitoringCategory.KVMS:
        project.addKvms(config as KvmConfiguration);
        this.floorplanService.addKvm(config as KvmConfiguration);
        break;
      case MonitoringCategory.DISPLAYS:
        project.addDisplays(config as DisplayConfiguration);
        this.floorplanService.addDisplay(config as DisplayConfiguration);
        break;
      case MonitoringCategory.UTILITY:
        project.addUtilities(config as UtilityConfiguration);
        this.floorplanService.addUtility(config as UtilityConfiguration);
        break;
    }
  }

  protected addToBom(result: MonitoringSearchResult) {
    if (!this.canEditProductList()) return;
    const rawProject = this.floorplanService.selectedFloorplan.project;
    if (!isMonitoringProject(rawProject)) return;
    this.monitoringProductListAddService.addToProductList(result, rawProject, this.deviceLookup);
  }

  protected showGroupDetails(result: MonitoringSearchResult) {
    if (!isGroupedOption(result.option)) return;
    const items: MonitoringGroupedPartSourceItem[] = [
      createMonitoringGroupedSourceItem({ option: result.option, quantity: 1 }, getMonitoringPrimaryLabel(result.category)),
    ];
    this.modalService.open(FloorplanMonitoringItemDetailsModalComponent, {
      size: ModalSize.LARGE,
      data: { items, category: result.category, title: result.option.groupName },
    });
  }

  protected openSelectPlaceholderMenu() {
    const categories = Object.values(MonitoringCategory);
    this.modalService
      .open(SelectMonitoringPlaceholderModalComponent, {
        data: categories,
        size: ModalSize.SMALL,
      })
      .onClose$.subscribe((category) => {
        if (!category) return;
        const placeholder = createMonitoringPlaceholder(category, this.floorplanService);
        placeholder.products = [new PlaceholderProduct("", this.searchValue)];
      });
  }
}
